import React, { useState, useEffect, useRef } from "react";
import LayerAdmin from "../LayerAdmin";
import { toast } from "react-toastify";
import ButtonLoader from "../../zamzam/components/ButtonLoader";
import { useNavigate, useParams } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import {
  getSpecificTeri,
  updateTeri,
  getDropdowns,
  getPlanningSessions,
  getRegions,
} from "../api/territory";
import Dropdown from "../components/Dropdown";

const EditTerritory = () => {
  const [formData, setFormData] = useState({
    id: "",
    emp_id: "",
    territory_identifier: "",
    region_entity_key: "",
    session_entity_key: "",
  });
  const [loading, setLoading] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [identifierValues, setIdentifierValues] = useState<any[]>([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const isInitialDataLoad = useRef(true);
  const prevRegionKey = useRef<string>("");
  const prevSessionKey = useRef<string>("");

  const handleChangeDropdown = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "region_entity_key") {
        updated.session_entity_key = "";
        updated.territory_identifier = "";
      }
      if (name === "session_entity_key") {
        updated.territory_identifier = "";
      }
      return updated;
    });
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return timestamp;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatOptionLabel = (label?: string, timestamp?: string) => {
    const safeLabel = label || "";
    const formattedTimestamp = formatTimestamp(timestamp);
    return formattedTimestamp ? `${safeLabel} (${formattedTimestamp})` : safeLabel;
  };

  const getLabelClass = (disabled: boolean) =>
    `block font-semibold mb-1 ${disabled ? "text-gray-400" : "text-gray-700"}`;

  const regionOptions = regions.map((region) => ({
    value:
      region.region_entity_key ||
      region.region_identifier ||
      region.id?.toString() ||
      "",
    name: formatOptionLabel(
      region.region_description ||
        region.region_identifier ||
        `Region ${region.region_identifier || region.region_entity_key}`,
      region.created_timestamp
    ),
  }));

  const sessionOptions = sessions.map((session) => ({
    value: session.session_entity_key,
    name: formatOptionLabel(
      session.session_description ||
        session.region_description ||
        `Session ${session.session_entity_key}`,
      session.created_timestamp
    ),
  }));

  const identifierOptions = identifierValues.map((identifier: any) => {
    const value =
      (typeof identifier === "string" && identifier) ||
      identifier?.territory_identifier ||
      identifier?.identifier ||
      identifier?.value ||
      identifier?.id?.toString() ||
      "";
    const label =
      (typeof identifier === "string" && identifier) ||
      identifier?.territory_description ||
      identifier?.territory_identifier ||
      identifier?.name ||
      value;
    return {
      value,
      name: formatOptionLabel(label, identifier?.created_timestamp),
    };
  });

  const isRegionDisabled = regionOptions.length === 0;
  // Planning Session is disabled if no region is selected OR no sessions available
  const isSessionDisabled = !formData.region_entity_key || sessionOptions.length === 0;
  // Territory Identifier is disabled if no session is selected (which requires region) OR no identifiers available
  const isIdentifierDisabled = !formData.session_entity_key || !formData.region_entity_key || identifierOptions.length === 0;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getInputClass = (field: string) =>
    `w-full border px-3 py-2 rounded ${invalidFields.includes(field) ? "border-red-500" : ""}`;

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const payload = {
      emp_id: formData.emp_id,
      territory_identifier: formData.territory_identifier,
      session_entity_key: formData.session_entity_key,
      region_entity_key: formData.region_entity_key,
    };

    try {
      setLoading(true);
      
      // Validate hierarchical dependencies
      if (!formData.region_entity_key) {
        setInvalidFields(["region_entity_key"]);
        toast.error("Please select a region first.");
        return;
      }
      if (!formData.session_entity_key) {
        setInvalidFields(["session_entity_key"]);
        toast.error("Please select a planning session.");
        return;
      }
      if (!formData.territory_identifier) {
        setInvalidFields(["territory_identifier"]);
        toast.error("Please select a territory identifier.");
        return;
      }

      const emptyFields = Object.entries(formData)
        .filter(([key, value]) => key !== "id" && (value === null || value === ""))
        .map(([key]) => key);

      setInvalidFields(emptyFields);

      if (emptyFields.length > 0) {
        toast.error("Please fill all required fields.");
        return;
      }

      const response = await updateTeri(payload, formData.id);
      if (!response) {
        console.warn("Invalid or empty response:", response);
        toast.error("Invalid or empty response");
        return;
      }
      toast.success("Territory updated successfully!");
      setTimeout(() => {
        navigate(URL_MAPPING.TERRITORY);
      }, 2000);
    } catch (error: any) {
      console.error("Error fetching territory data:", error);
      if (error?.response?.status === 404) {
        toast.error("Route not found, Please try again later", {
          autoClose: false,
          closeOnClick: true,
          draggable: true,
        });
      } else if (error?.response?.status === 500) {
        toast.error("Error while fetching the territory", {
          autoClose: false,
          closeOnClick: true,
          draggable: true,
        });
      } else {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach((err) =>
            toast.error(err.msg, {
              autoClose: false,
              closeOnClick: true,
              draggable: true,
            })
          );
        } else {
          toast.error(detail?.msg || detail || "An unexpected error occurred.", {
            autoClose: false,
            closeOnClick: true,
            draggable: true,
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await getRegions();
        if (!res || !Array.isArray(res)) {
          toast.error("Failed to load regions");
          setRegions([]);
          return;
        }
        setRegions(res);
      } catch (error: any) {
        console.error("Failed to fetch regions:", error);
        if (error?.response?.status === 404) {
          toast.error("Regions not found, Please try again later");
        } else if (error?.response?.status === 500) {
          toast.error("Error while fetching regions");
        } else {
          const detail = error?.response?.data?.detail;
          if (Array.isArray(detail)) {
            detail.forEach((err: any) => toast.error(err.msg));
          } else {
            toast.error(detail?.msg || detail || "An unexpected error occurred.");
          }
        }
      }
    };

    fetchRegions();
  }, []);

  useEffect(() => {
    // Don't fetch on initial data load, only when user changes the region
    if (isInitialDataLoad.current) {
      prevRegionKey.current = formData.region_entity_key;
      return;
    }

    // Only fetch if region actually changed (user selection)
    if (prevRegionKey.current === formData.region_entity_key) {
      return;
    }

    prevRegionKey.current = formData.region_entity_key;

    if (!formData.region_entity_key) {
      setSessions([]);
      return;
    }

    const fetchSessions = async () => {
      try {
        const res = await getPlanningSessions(formData.region_entity_key);
        if (!res || !Array.isArray(res)) {
          toast.error("Failed to load planning sessions");
          setSessions([]);
          return;
        }
        setSessions(res);
      } catch (error: any) {
        console.error("Failed to fetch planning sessions:", error);
        if (error?.response?.status === 404) {
          toast.error("Planning sessions not found, Please try again later");
        } else if (error?.response?.status === 500) {
          toast.error("Error while fetching planning sessions");
        } else {
          const detail = error?.response?.data?.detail;
          if (Array.isArray(detail)) {
            detail.forEach((err: any) => toast.error(err.msg));
          } else {
            toast.error(detail?.msg || detail || "An unexpected error occurred.");
          }
        }
      }
    };

    fetchSessions();
  }, [formData.region_entity_key]);

  useEffect(() => {
    // Don't fetch on initial data load, only when user changes the session
    if (isInitialDataLoad.current) {
      prevSessionKey.current = formData.session_entity_key;
      return;
    }

    // Only fetch if session actually changed (user selection)
    if (prevSessionKey.current === formData.session_entity_key) {
      return;
    }

    prevSessionKey.current = formData.session_entity_key;

    if (!formData.session_entity_key || !formData.region_entity_key) {
      setIdentifierValues([]);
      return;
    }

    const fetchIdentifiers = async () => {
      try {
        const res = await getDropdowns(formData.session_entity_key);
        if (!res || !Array.isArray(res)) {
          toast.error("Failed to load territory identifiers");
          setIdentifierValues([]);
          return;
        }
        setIdentifierValues(res);
      } catch (error: any) {
        console.error("Failed to fetch territory identifiers:", error);
        if (error?.response?.status === 404) {
          toast.error("Territory identifiers not found, Please try again later");
        } else if (error?.response?.status === 500) {
          toast.error("Error while fetching territory identifiers");
        } else {
          const detail = error?.response?.data?.detail;
          if (Array.isArray(detail)) {
            detail.forEach((err: any) => toast.error(err.msg));
          } else {
            toast.error(detail?.msg || detail || "An unexpected error occurred.");
          }
        }
      }
    };

    fetchIdentifiers();
  }, [formData.session_entity_key]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getSpecificTeri(id as string);
        if (!response) {
          console.warn("Invalid or empty  response:", response);
          toast.error("Invalid or empty  response");
          isInitialDataLoad.current = false;
          return;
        }
        const data = response;
        setFormData({
          id: data.id,
          emp_id: data.emp_id || "",
          territory_identifier: data.territory_identifier || "",
          session_entity_key: data.session_entity_key || "",
          region_entity_key: data.region_entity_key || "",
        });
        // Set previous values to current values after initial load
        prevRegionKey.current = data.region_entity_key || "";
        prevSessionKey.current = data.session_entity_key || "";
        // Mark initial data load as complete - now user changes will trigger API calls
        isInitialDataLoad.current = false;
      } catch (error: any) {
        console.error("Error fetching customer data:", error);
        isInitialDataLoad.current = false;
        if (error?.response?.status === 404) {
          toast.error("Route not found, Please try again later");
        } else if (error?.response?.status === 500) {
          toast.error("Error while fetching the employee");
        } else {
          const detail = error?.response?.data?.detail;
          if (Array.isArray(detail)) {
            detail.forEach((err) => toast.error(err.msg));
          } else {
            toast.error(detail?.msg || detail || "An unexpected error occurred.");
          }
        }
      }
    };
    fetchData();
  }, [id]);

  return (
    <LayerAdmin>
      <div className="flex items-center px-5 pt-5 gap-3">
        <button
          onClick={() => window.history.back()}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            version="1.1"
            width="10"
            height="10"
            viewBox="0 0 256 256"
            xmlSpace="preserve"
          >
            <g
              style={{
                stroke: "none",
                strokeWidth: 0,
                strokeDasharray: "none",
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeMiterlimit: 10,
                fill: "none",
                fillRule: "nonzero",
                opacity: 1,
              }}
              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
            >
              <polygon
                points="20.48,45 59.75,90 69.52,81.48 37.69,45 69.52,8.52 59.75,0 "
                style={{
                  stroke: "none",
                  strokeWidth: 1,
                  strokeDasharray: "none",
                  strokeLinecap: "butt",
                  strokeLinejoin: "miter",
                  strokeMiterlimit: 10,
                  fill: "rgb(0,0,0)",
                  fillRule: "nonzero",
                  opacity: 1,
                }}
                transform="  matrix(1 0 0 1 0 0) "
              />
            </g>
          </svg>
        </button>
        <h3 className="text-xl font-bold text-gray-700">Edit Territory</h3>
      </div>
      <form onSubmit={handleSubmit} className="p-4 lg:w-1/3 sm:w-full">
        <div className="my-2">
          <label className="block font-semibold mb-1">Territory ID</label>
          <input name="id" disabled value={formData.id} />
        </div>

        <div className="my-2">
          <label className="block font-semibold mb-1">Employee ID </label>
          <input
            type="text"
            name="emp_id"
            disabled
            value={formData.emp_id}
            onChange={() => {}}
            className={getInputClass("emp_id")}
            placeholder="Employee Id"
          />
        </div>

        <div className="my-2">
          <label className={getLabelClass(isRegionDisabled)}>Region *</label>
          <Dropdown
            name="region_entity_key"
            value={formData.region_entity_key}
            onChange={handleChangeDropdown}
            options={regionOptions}
            className={getInputClass("region_entity_key")}
            placeholder="Select a region"
            searchable={true}
            disabled={isRegionDisabled}
          />
        </div>
        <div className="my-2">
          <label className={getLabelClass(isSessionDisabled)}>
            Planning Session*
          </label>
          <Dropdown
            name="session_entity_key"
            value={formData.session_entity_key}
            onChange={handleChangeDropdown}
            options={sessionOptions}
            className={getInputClass("session_entity_key")}
            placeholder="Select a session"
            searchable={true}
            disabled={isSessionDisabled}
          />
        </div>

        <div className="my-2">
          <label className={getLabelClass(isIdentifierDisabled)}>
            Territory Identifiers *
          </label>

          <Dropdown
            name="territory_identifier"
            value={formData.territory_identifier}
            onChange={handleChangeDropdown}
            options={identifierOptions}
            className={getInputClass("territory_identifier")}
            placeholder="Select Identifiers "
            searchable={true}
            disabled={isIdentifierDisabled}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2"
        >
          Update Territory {loading && <ButtonLoader />}
        </button>
      </form>
    </LayerAdmin>
  );
};

export default EditTerritory;
